import type {
  CandidateDetailsCredential,
  CandidateDetailsField,
  CandidateDetailsProcedureGroup,
} from '@tempmee/react-blocks';
import {
  OutlineCarIcon,
  OutlineIdentificationIcon,
  OutlineShieldCheckIcon,
} from '@tempmee/react-icons';
import { AuthorizationStatus, type IAuthorization } from '~/domain/Credentials/types/Credentials';
import { getCandidateSpecialties } from '~/hooks/useCandidate';
import { IJob, IUser, ProfessionTypeEnum } from '~/models';
import { EducationUtils } from '~/utils/EducationUtils';

/**
 * Leading glyph for a verified credential.
 *
 * The block takes the icon from the caller on purpose — the design draws a
 * different glyph per credential rather than one generic check — and we were
 * passing none, so every row rendered bare.
 *
 * Matched on keyword rather than the exact name because the copy varies by
 * profession and is rewritten upstream ("State RDH License Number" reaches this
 * function as "State RDH License").
 *
 * The three glyphs and the fallback are the design's (Natalie, 2026-09-15): a
 * credential outside the named families reuses the background-check shield until
 * she draws it one of its own, rather than rendering bare.
 */
const toCredentialIcon = (label: string): CandidateDetailsCredential['icon'] => {
  if (/driver/i.test(label)) {
    return <OutlineCarIcon />;
  }

  if (/license/i.test(label)) {
    return <OutlineIdentificationIcon />;
  }

  return <OutlineShieldCheckIcon />;
};

/**
 * Verified credentials → the badged items above the Credentials tab's divider.
 *
 * Same filter the legacy `VerifiedCredentials` list applies: verified, not a
 * draft, and de-duplicated by name — a professional authorized in several states
 * carries the same credential more than once, and the tab is about what they
 * hold, not how many times over. Names are tidied the same way too, so
 * "NPI Number" reads as "NPI License" rather than as a form field.
 *
 * Each row carries the glyph `toCredentialIcon` picks for it.
 */
export const toCandidateCredentials = (
  authorizations: IAuthorization[] | null | undefined,
): CandidateDetailsCredential[] =>
  (authorizations ?? [])
    .flatMap((authorization) =>
      (authorization.credentials ?? []).filter(
        (credential) => credential.status === AuthorizationStatus.Verified && !credential.isDraft,
      ),
    )
    .filter(
      (credential, index, all) =>
        all.findIndex((other) => other.credentialName === credential.credentialName) === index,
    )
    .map((credential) => {
      const label = credential.credentialName
        .replaceAll('NPI Number', 'NPI License')
        .replaceAll(' Number', '');

      return { label, icon: toCredentialIcon(label) };
    });

/**
 * Candidate → the Procedures tab's groups.
 *
 * The shape of `specialtiesAndProcedures` depends on the profession, which is
 * the trap here. For RDH and FD the procedures are **top-level keys** prefixed
 * `procedure_`, each holding an empty array; for DA and DN they are the
 * **values** filed under each `specialty_` key.
 * `EducationUtils.getProceduresOfProfession` already knows both shapes, so this
 * defers to it rather than inferring from the map — reading it as if it were
 * always the DA/DN shape leaves every RDH candidate with an empty tab.
 *
 * Grouping follows the data rather than inventing structure: DA/DN get one group
 * per specialty, because that is how their procedures are filed, and RDH/FD get
 * a single group — their list is flat, and the legacy screen titled that same
 * list "Experienced Procedures".
 */
export const toProcedureGroups = (
  candidate: IUser | null | undefined,
  job: Partial<IJob> | null | undefined,
): CandidateDetailsProcedureGroup[] => {
  const profession = (job?.profession || candidate?.license?.profession) as ProfessionTypeEnum;

  return EducationUtils.toProcedureGroups({
    specialtiesAndProcedures:
      candidate?.professional_info?.education?.[profession]?.specialtiesAndProcedures,
    profession,
    // Scoped to the shift: a DA/DN candidate is grouped by the specialties this
    // shift is about, not by everything they have ever filed.
    specialties: getCandidateSpecialties(candidate, job),
    flatLabel: 'Experienced Procedures',
  });
};

/** Candidate's specialty chips, prefix-stripped for display. */
export const toSpecialtyChips = (
  candidate: IUser | null | undefined,
  job: Partial<IJob> | null | undefined,
): string[] => EducationUtils.mapToRemoveSpecialtyPrefix(getCandidateSpecialties(candidate, job));

/**
 * The candidate's licence details → the rows below the Credentials tab's divider.
 *
 * Every row is dropped when its value is missing rather than rendered empty: a
 * blank "NPI" reads as data the office is owed, when in fact the field does not
 * apply to that profession.
 */
export const toCandidateFields = ({
  licenseNumber,
  issuedYear,
  licenseState,
  nationalProviderIdentifier,
  shouldShowNpi,
}: {
  licenseNumber?: string;
  /** Already formatted — the panel shows the year alone. */
  issuedYear?: string;
  licenseState?: string;
  nationalProviderIdentifier?: string;
  /** NPI is a dentist-only credential. */
  shouldShowNpi?: boolean;
}): CandidateDetailsField[] =>
  (
    [
      licenseNumber ? { label: 'License #', value: licenseNumber } : null,
      issuedYear ? { label: 'Issued', value: issuedYear } : null,
      licenseState ? { label: 'State', value: licenseState } : null,
      shouldShowNpi && nationalProviderIdentifier
        ? { label: 'NPI', value: nationalProviderIdentifier }
        : null,
    ] as (CandidateDetailsField | null)[]
  ).filter((field): field is CandidateDetailsField => field !== null);
